import React, {
    createContext,
    type PropsWithChildren,
    type RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import { type HslaColor, type HslColor } from 'react-colorful';
import { type ColorStop, type Gradient, LinearGradient } from '../../types';
import { type Action, reducer } from './gradientReducer';
import { solidColorToHslColor } from '../../utils/hsl';
import { type SaturationRef } from '../SolidColorPicker/Saturation';
import { type HueAndOpacityRef } from '../SolidColorPicker/HueAndOpacity';
import { type SliderRef } from './Slider';
import { type CommonGradientOptionsRef, type ThumbnailRef } from './GradientOptions';

type GradientContextValue = {
    state: {
        gradient: Gradient;
        selected: number;
    };
    dispatch: (action: Action) => void;
    updateSelectedStop: (stopData: Partial<ColorStop>) => void;
    changeAngle: (angle: number) => void;
    currentColorValue: HslaColor | HslColor;
    saturationRef: RefObject<SaturationRef>;
    hueAndOpacityRef: RefObject<HueAndOpacityRef>;
    sliderRef: RefObject<SliderRef>;
    thumbnailRef: RefObject<ThumbnailRef>;
    commonGradientOptionsRef: RefObject<CommonGradientOptionsRef>;
};

const GradientContext = createContext<GradientContextValue | null>(null);

type ProviderProps = PropsWithChildren<{
    defaultValue: Gradient;
    onChange: (gradient: Gradient) => void;
}>;

export function GradientProvider({ children, defaultValue, onChange }: ProviderProps) {
    const saturationRef = useRef<SaturationRef>(null);
    const hueAndOpacityRef = useRef<HueAndOpacityRef>(null);
    const sliderRef = useRef<SliderRef>(null);
    const thumbnailRef = useRef<ThumbnailRef>(null);
    const commonGradientOptionsRef = useRef<CommonGradientOptionsRef>(null);
    const [state, dispatch] = useReducer(reducer, {
        gradient: defaultValue,
        selected: 0,
    });

    const currentColorStop = state.gradient.stops[state.selected];
    const currentColor =
        typeof currentColorStop.color === 'string' ? currentColorStop.color : currentColorStop.color.color;
    const currentOpacity = typeof currentColorStop.color === 'string' ? undefined : currentColorStop.color.opacity;

    const currentColorValue = useMemo(
        () => solidColorToHslColor(currentColor, currentOpacity, state.latestHue),
        [currentColor, currentOpacity, state.latestHue],
    );

    const updateSelectedStop = useCallback(
        (stopData: Partial<ColorStop>) => {
            const newStops = [...state.gradient.stops];
            const stop = newStops[state.selected];
            newStops[state.selected] = { ...stop, ...stopData };

            state.gradient = {
                ...state.gradient,
                stops: newStops,
            };

            onChange(state.gradient);
        },
        [state, onChange],
    );

    const changeAngle = useCallback(
        (angle: number) => {
            dispatch({ type: 'change-angle', payload: angle });
            (state.gradient as LinearGradient).angle = angle;
            onChange(state.gradient);
        },
        [state, onChange],
    );

    const contextValue = useMemo(() => {
        return {
            state,
            updateSelectedStop,
            changeAngle,
            currentColorValue,
            dispatch,
            saturationRef,
            hueAndOpacityRef,
            sliderRef,
            thumbnailRef,
            commonGradientOptionsRef,
        };
    }, [state, updateSelectedStop, changeAngle, currentColorValue]);

    useEffect(() => {
        dispatch({
            type: 'change-gradient',
            payload: defaultValue,
        });
    }, [defaultValue]);

    return <GradientContext.Provider value={contextValue}>{children}</GradientContext.Provider>;
}

export function useGradient() {
    const gradient = useContext(GradientContext);

    if (!gradient) {
        throw new Error('useGradient must be used within a GradientProvider');
    }

    return gradient;
}
